import {
  IntegrationWithJobInfo,
  getIntegrationsWithJobs,
  findEmailIntegrationForUsername,
  LastSyncJobInfo,
  UnfiConfig,
} from "@/api/integration"
import { Button } from "@/components/ui/button"
import { useState } from "react"
import { SettingsLayout } from "./layout"
import { Section } from "./index"
import { useAsyncEffect, usePageTitle } from "@/utils"
import useLocation from "wouter-preact/use-location"
import { ConnectionCard } from "./connection-card"
import { loadOrgSettings } from "@/org_settings/state.tsx"
import { MailboxConnectPanel } from "@/connect_mailbox/mailbox_connect_panel"
import { ReconnectMailboxes } from "@/connect_mailbox/reconnect_mailboxes"

export function SettingsConnectionsPage() {
  usePageTitle("Connections")
  return (
    <SettingsLayout>
      {/* Above the list: a mailbox that has stopped syncing is the one thing here only the brand can fix. */}
      <ReconnectMailboxes className="mb-6" />
      <ConnectionSettingsSection />
      <Section title="Connect a mailbox">
        <p class="text-sm text-gray-500 mb-4">
          Connect the mailbox your deduction and remittance email arrives in. We'll only ever sync
          the mailbox named here — if a different account signs in, the connection is refused unless
          that account provably has access to it.
        </p>
        <MailboxConnectPanel />
      </Section>
    </SettingsLayout>
  )
}

function ConnectionSettingsSection() {
  const [, setLocation] = useLocation()

  return (
    <Section
      title="Connections"
      action={
        <Button variant="outline" onClick={() => setLocation("/settings/connect")} class="text-sm">
          Add Connection
        </Button>
      }>
      <ConnectionSettings />
    </Section>
  )
}

/**
 * The job that answers "when did this connection last sync?".
 *
 * An email-driven connection that runs its own scheduled sync — Thrive, Southern Glazer's,
 * Target, Chex, GFI — keeps that job on its own integration id, so its own job is the answer.
 * Only the connections with no job of their own, like UNFI, whose deductions arrive with the mail
 * itself, fall through to the mailbox that feeds them. Without the first branch a healthy Thrive
 * sync read as "Not synced yet", because Thrive's placeholder username ("default") matches no
 * mailbox.
 *
 * The `owns_sync_job` branch is the other half, and the important one: a connection that owns its
 * sync and has *never run it* must say so. Falling through to the mailbox told One Trick Pony that
 * Chex was "Synced Just now" for the seven months the Chex ingest did not exist, and still tells
 * all nine Target brands the same today — `sync-target-from-email` has never run once in
 * production. A green badge over an integration that has booked nothing is worse than no badge:
 * it is the reason nobody looked.
 */
export function pickLastSyncJob(
  integration: IntegrationWithJobInfo,
  integrations: IntegrationWithJobInfo[]
): LastSyncJobInfo | undefined {
  if (integration.last_sync_job) {
    return integration.last_sync_job
  }
  if (!integration.syncs_via_email || integration.owns_sync_job) {
    return undefined
  }
  const emailAddr =
    integration.name === "unfi" ? (integration.data as unknown as UnfiConfig).email_address : integration.username
  if (!emailAddr) {
    return undefined
  }
  return findEmailIntegrationForUsername(integrations, emailAddr)?.last_sync_job
}

function ConnectionSettings() {
  const [integrations, setIntegrations] = useState<IntegrationWithJobInfo[]>([])

  useAsyncEffect(async () => {
    // Refresh org settings in case an integration was just connected/disconnected
    loadOrgSettings()
    const res = await getIntegrationsWithJobs()
    if (!res.ok) {
      return
    }
    setIntegrations(res.value.data)
  }, [])

  const visibleIntegrations = integrations.filter(i => !i.secondary)

  if (visibleIntegrations.length === 0) {
    return (
      <div class="text-center py-8 text-gray-500 text-sm">No connections yet. Add a connection to get started.</div>
    )
  }

  return (
    <div class="space-y-3 pt-2">
      {visibleIntegrations.map(integration => (
        <ConnectionCard
          key={integration.id}
          integration={integration}
          lastJob={pickLastSyncJob(integration, integrations)}
        />
      ))}
    </div>
  )
}
